import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@medical/shared/contexts/AuthContext';

const PlayerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8fafc;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
  max-width: 250px;
`;

const PlayButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: #2563eb;
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AudioInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
`;

const AudioLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const AudioDuration = styled.div`
  font-size: 0.625rem;
  color: #6b7280;
  font-family: monospace;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  cursor: pointer;
  margin-top: 0.25rem;
`;

const Progress = styled.div`
  height: 100%;
  background: #3b82f6;
  width: ${props => props.progress}%;
  transition: width 0.1s linear;
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: #ef4444;
  padding: 0.25rem;
`;

const AudioPlayer = ({ audioNote, token: providedToken = null }) => {
  const { t } = useTranslation();
  const { token: authToken } = useAuth();
  // Use provided token (for doctor view) or fall back to auth context token (for patient view)
  const token = providedToken || authToken;
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchAudio = async () => {
      if (audioNote && audioNote.filename && token) {
        try {
          setLoading(true);
          setError(null);
          
          console.log('[AudioPlayer] Full audioNote object:', JSON.stringify(audioNote, null, 2));
          console.log('[AudioPlayer] Fetching audio file:', audioNote.filename);
          console.log('[AudioPlayer] API endpoint:', `/api/pain-logs/audio/${audioNote.filename}`);
          
          // Fetch audio file with authentication
          const response = await axios.get(`/api/pain-logs/audio/${audioNote.filename}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            responseType: 'blob'
          });
          
          console.log('[AudioPlayer] Audio response received:', {
            status: response.status,
            headers: response.headers,
            contentType: response.headers['content-type'],
            type: response.data.type,
            size: response.data.size
          });
          
          // Verify we got actual audio data
          if (response.data.size === 0) {
            throw new Error('Received empty audio file');
          }
          
          // Create blob URL from response with explicit audio type
          const contentType = response.headers['content-type'] || 'audio/webm';
          const blob = new Blob([response.data], { type: contentType });
          const url = URL.createObjectURL(blob);
          
          console.log('[AudioPlayer] Created blob:', {
            blobSize: blob.size,
            blobType: blob.type,
            url: url
          });
          
          setAudioBlob(blob);
          setAudioUrl(url);
          setLoading(false);
        } catch (err) {
          console.error('[AudioPlayer] Error fetching audio:', {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status,
            stack: err.stack
          });
          setError(t('audioPlayer.errors.loadFailed', { message: err.message, defaultValue: `Failed to load audio: ${err.message}` }));
          setLoading(false);
        }
      } else {
        console.log('[AudioPlayer] Missing required data:', {
          hasAudioNote: !!audioNote,
          hasFilename: audioNote?.filename,
          hasToken: !!token
        });
      }
    };

    fetchAudio();

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioNote, token]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    if (!audioUrl) {
      setError(t('audioPlayer.errors.notLoaded', { defaultValue: 'Audio not loaded yet' }));
      return;
    }

    if (!audioRef.current) {
      console.log('Creating new Audio element with URL:', audioUrl);
      audioRef.current = new Audio(audioUrl);
      
      audioRef.current.addEventListener('loadedmetadata', () => {
        console.log('Audio metadata loaded, duration:', audioRef.current.duration);
        setDuration(audioRef.current.duration);
      });

      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime);
      });

      audioRef.current.addEventListener('ended', () => {
        console.log('Audio playback ended');
        setIsPlaying(false);
        setCurrentTime(0);
      });

      audioRef.current.addEventListener('error', (e) => {
        console.error('Audio element error:', {
          error: e,
          audioError: audioRef.current?.error,
          code: audioRef.current?.error?.code,
          message: audioRef.current?.error?.message
        });
        setError(t('audioPlayer.errors.playFailed', { defaultValue: 'Failed to play audio' }));
        setIsPlaying(false);
      });

      audioRef.current.addEventListener('canplay', () => {
        console.log('Audio can play');
      });
    }

    try {
      if (isPlaying) {
        console.log('Pausing audio');
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        console.log('Starting audio playback');
        await audioRef.current.play();
        setIsPlaying(true);
        setError(null);
      }
    } catch (err) {
      console.error('Playback error:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setError(t('audioPlayer.errors.playFailedWithMessage', { message: err.message, defaultValue: `Failed to play audio: ${err.message}` }));
      setIsPlaying(false);
    }
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current) return;
    
    const bounds = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - bounds.left;
    const percentage = clickX / bounds.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  if (!audioNote) {
    return null;
  }

  if (error) {
    return (
      <PlayerContainer>
        <ErrorText>❌ {error}</ErrorText>
      </PlayerContainer>
    );
  }

  if (loading) {
    return (
      <PlayerContainer>
        <AudioInfo>
          <AudioLabel>🔄 {t('audioPlayer.loading', { defaultValue: 'Loading audio...' })}</AudioLabel>
        </AudioInfo>
      </PlayerContainer>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <PlayerContainer>
      <PlayButton onClick={handlePlayPause} disabled={!audioUrl}>
        {isPlaying ? '⏸' : '▶️'}
      </PlayButton>
      
      <AudioInfo>
          <AudioLabel>
            🎤 {t('audioPlayer.label', { defaultValue: 'Voice Note' })}
          </AudioLabel>
        <AudioDuration>
          {formatTime(currentTime)} / {formatTime(duration || audioNote.duration)}
        </AudioDuration>
        <ProgressBar onClick={handleProgressClick}>
          <Progress progress={progress} />
        </ProgressBar>
      </AudioInfo>
    </PlayerContainer>
  );
};

export default AudioPlayer;

